import { faUser, faArrowLeft, faCalendarAlt, faChartLine, faHistory } from '@fortawesome/free-solid-svg-icons';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDispatch, useSelector } from 'react-redux';
import { useState, useEffect, useMemo } from 'react';
import cn from 'classnames';
import { useSearchParamsUpdater } from 'hooks/useSearchParamsUpdater';
import { calculateWorkingTime, closeActiveSession, getUserSessions } from '@redux/features/sessionsSlice';
import { addNotification } from '@redux/features/notificationsSlice';
import { getUser } from '@redux/features/usersSlice';
import { formatDate, formatDateTimeUA } from 'helpers/formatDate';
import { getEntityLink } from 'helpers/getEntityLink';
import { accessLevels } from 'constants/accessLevels';
import { DateRangePicker } from 'components/DateRangePicker';
import { BackButton } from 'components/BackButton';
import { Tooltip } from 'components/Tooltip';
import { Button } from 'components/Button';
import { UserLastActivityBadge } from './UserLastActivityBadge';
import { UserStatusBadge } from './UserStatusBadge';
import { SessionHistory } from './SessionHistory';
import { WorkTimeChart } from './WorkTimeChart';
import { WorkTimeStats } from './WorkTimeStats';
import styles from './UserPage.scss';

export const UserPage = () => {
  const { user: currentUser } = useSelector((state) => state.auth);
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const checkAccess = () => {
    if (location.pathname.includes('/users/') && currentUser?.role !== 3) {
      dispatch(
        addNotification({
          id: Math.floor(Math.random()),
          title: 'У вас немає доступу до цієї сторінки',
          text: 'Необхідний рівень доступу: Керівник',
          type: 'info',
        })
      );
      navigate('/profile', { replace: true });
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (currentUser) checkAccess();
  }, [location.pathname, currentUser]);

  const { sessions, workTime, error } = useSelector((state) => state.sessions);
  const isLoading = useSelector((state) => state.users.isLoading);
  const users = useSelector((state) => state.users.users);
  const query = useSearchParamsUpdater();
  const selectedTab = query.getParam('tab');
  const { userID: paramUserID } = useParams();

  const resolvedUserID = paramUserID || currentUser?.id;

  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)),
    endDate: new Date(),
  });

  const daysDiff = useMemo(
    () => Math.ceil((dateRange.endDate - dateRange.startDate) / (1000 * 60 * 60 * 24)),
    [dateRange]
  );

  useEffect(() => {
    if (daysDiff < 7 && selectedTab === 'analytics') {
      query.deleteParam('tab');
    }
  }, [daysDiff]);

  const user = useMemo(() => users.find((u) => u.id === parseInt(resolvedUserID)), [users, resolvedUserID]);

  useEffect(() => {
    if (resolvedUserID && !user) {
      dispatch(getUser(resolvedUserID));
    }
  }, [resolvedUserID, user]);

  useEffect(() => {
    if (resolvedUserID && dateRange.startDate && dateRange.endDate) {
      dispatch(
        getUserSessions({
          userID: resolvedUserID,
          params: {
            startDate: formatDate(dateRange.startDate),
            endDate: formatDate(dateRange.endDate),
            limit: 100,
          },
        })
      );
      dispatch(
        calculateWorkingTime({
          userID: resolvedUserID,
          params: {
            startDate: formatDate(dateRange.startDate),
            endDate: formatDate(dateRange.endDate),
          },
        })
      );
    }
  }, [resolvedUserID, dateRange]);

  const handleCloseActiveSession = (sessionID) => dispatch(closeActiveSession(sessionID));

  const canManageUser = () => currentUser?.role === 3 && currentUser?.id !== parseInt(resolvedUserID);

  if (isLoading) {
    return <div className={styles.loading}>Завантаження...</div>;
  }

  if (error || !user) {
    return (
      <div className={styles.error}>
        <p>{error || 'Користувача не знайдено'}</p>
        <Button text='Повернутися' onClick={() => navigate('/users')} icon={faArrowLeft} />
      </div>
    );
  }

  const activeSession = sessions.find((session) => session.isActive);

  return (
    <div className={styles.page}>
      {location.pathname.includes('/users') && <BackButton path={getEntityLink('users')} text='Персонал' />}

      <div className={styles.header}>
        <div className={styles.userInfo}>
          <div className={styles.titleWrapper}>
            <h1>
              <FontAwesomeIcon icon={faUser} />
              {user.surname} {user.name}
            </h1>
            <p>{user.email}</p>
          </div>
          <UserStatusBadge userID={resolvedUserID} />
        </div>

        {activeSession && canManageUser() && (
          <Button
            text='Завершити сесію'
            onClick={() => handleCloseActiveSession(activeSession.id)}
            className={styles.closeSessionBtn}
            smallStyle
          />
        )}
      </div>

      <div className={styles.userMeta}>
        <div className={styles.metaItem}>
          <strong>Роль: </strong>
          {accessLevels.find((l) => l.value === user.role).label}
        </div>
        {user.role !== 3 && (
          <div className={styles.metaItem}>
            <strong>Локації: </strong>
            {user.locations && user.locations.length > 0
              ? user.locations.map((loc) => loc.name).join(', ')
              : 'Не призначено'}
          </div>
        )}
        {user.lastLogin && (
          <div className={styles.metaItem}>
            <strong>Останній вхід: </strong>
            {formatDateTimeUA(user.lastLogin, true)}
          </div>
        )}
        <UserLastActivityBadge userID={resolvedUserID} />
      </div>

      <div className={styles.controls}>
        <div>
          <span className={styles.title}>Інформація за:</span>
          <DateRangePicker startDate={dateRange.startDate} endDate={dateRange.endDate} onChange={setDateRange} />
        </div>

        <div className={styles.tabs}>
          <button
            className={cn(styles.tab, { [styles.active]: !selectedTab || selectedTab === 'stats' })}
            onClick={() => query.setParam('tab', 'stats')}
            disabled={!selectedTab || selectedTab === 'stats'}
          >
            <FontAwesomeIcon icon={faCalendarAlt} />
            Графік роботи
          </button>
          <button
            className={cn(styles.tab, { [styles.active]: selectedTab === 'analytics' })}
            onClick={() => query.setParam('tab', 'analytics')}
            disabled={selectedTab === 'analytics' || daysDiff < 7}
          >
            <FontAwesomeIcon icon={faChartLine} />
            Аналітика
            <Tooltip className={styles.tooltip} text='Доступно для періодів від 7 днів' />
          </button>
          <button
            className={cn(styles.tab, { [styles.active]: selectedTab === 'history' })}
            onClick={() => query.setParam('tab', 'history')}
            disabled={selectedTab === 'history'}
          >
            <FontAwesomeIcon icon={faHistory} />
            Історія сесій
          </button>
        </div>
      </div>

      {(!selectedTab || selectedTab === 'stats') && workTime && (
        <WorkTimeStats data={workTime} activeSession={activeSession} dateRange={dateRange} />
      )}

      {selectedTab === 'analytics' && workTime && <WorkTimeChart data={workTime} dateRange={dateRange} />}

      {selectedTab === 'history' && (
        <SessionHistory sessions={sessions} canManage={canManageUser()} onCloseSession={handleCloseActiveSession} />
      )}
    </div>
  );
};
